/**
 * <uterific-scroll> — maps how far a tall section has travelled past the viewport onto a
 * single eased progress value, which its CSS reads as custom properties.
 *
 * The scroll-choreography guideline asks every pinned section to be driven by ONE progress
 * value so reveals overlap, reverse on scroll-up and stay in step. This element is that
 * value:
 *
 *   --ut-p   0 → 1 across the section's travel, written on the host
 *   --ut-t   per-child eased progress, written on every [data-ut-step] child
 *
 * Children stagger by `step-offset` and each takes `step-span` of the progress to arrive,
 * so child n starts before child n-1 has finished. Counting starts as soon as the
 * section's top crosses into the viewport rather than once it pins, which is what makes
 * the panels enter early instead of waiting for the pin.
 *
 * Attributes:
 *   head          fraction of the viewport used as the lead-in (default 0.35)
 *   step-offset   progress between one child starting and the next (default 0.18)
 *   step-span     progress a single child takes to arrive (default 0.34)
 *
 * Reduced motion: children are placed in their arrived state and scroll is not tracked.
 *
 * Load this with type="module". It shares helper names with uterific-exploded.js, so as a
 * classic script whichever of the two parsed second would die on a redeclaration.
 */

const clamp = (v) => (v < 0 ? 0 : v > 1 ? 1 : v);
const easeOut = (t) => 1 - Math.pow(1 - t, 3);

class UterificScroll extends HTMLElement {
  connectedCallback() {
    this.steps = Array.from(this.querySelectorAll('[data-ut-step]'));

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.style.setProperty('--ut-p', '1');
      this.steps.forEach((step) => step.style.setProperty('--ut-t', '1'));
      return;
    }

    this.head = parseFloat(this.getAttribute('head')) || 0.35;
    this.stepOffset = parseFloat(this.getAttribute('step-offset')) || 0.18;
    this.stepSpan = parseFloat(this.getAttribute('step-span')) || 0.34;

    this.frame = 0;
    this.onScroll = () => {
      if (!this.frame) this.frame = requestAnimationFrame(() => this.measure());
    };

    this.measure();
    // Above 990px Horizon scrolls inside .page-wrapper, not the window, and scroll events
    // do not bubble — a window listener would never fire and the section would sit at
    // progress 0 forever. Capturing on the document catches scroll from the page wrapper
    // and from the window alike, so this works whichever element is doing the scrolling.
    document.addEventListener('scroll', this.onScroll, { passive: true, capture: true });
    window.addEventListener('resize', this.onScroll);
  }

  disconnectedCallback() {
    document.removeEventListener('scroll', this.onScroll, { capture: true });
    window.removeEventListener('resize', this.onScroll);
    if (this.frame) cancelAnimationFrame(this.frame);
  }

  measure() {
    this.frame = 0;
    const rect = this.getBoundingClientRect();
    // The lead-in means progress starts the moment the top edge enters the viewport, so
    // the first child is already moving before the frame pins.
    const travel = rect.height - window.innerHeight * this.head;
    const p =
      travel <= 0
        ? 1
        : clamp((window.innerHeight * (1 - this.head) - rect.top) / travel);

    this.style.setProperty('--ut-p', p.toFixed(4));

    this.steps.forEach((step, i) => {
      const t = easeOut(clamp((p - i * this.stepOffset) / this.stepSpan));
      step.style.setProperty('--ut-t', t.toFixed(4));
    });
  }
}

if (!customElements.get('uterific-scroll')) {
  customElements.define('uterific-scroll', UterificScroll);
}
